import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

const LOCAL_DEV_HOSTS = new Set(["localhost", "127.0.0.1", "::1"]);

export function resolveDevAuthToken({ nodeEnv, hostname, envToken, storageToken } = {}) {
    if (nodeEnv !== "development") return null;
    if (!LOCAL_DEV_HOSTS.has(hostname)) return null;
    return envToken || storageToken || null;
}

function readDevAuthToken() {
    if (process.env.NODE_ENV !== "development") return null;
    const hostname = typeof window !== "undefined" ? window.location.hostname : "";
    const storageToken = typeof localStorage !== "undefined" ? localStorage.getItem("nerdy_dev_token") : null;
    return resolveDevAuthToken({
        nodeEnv: process.env.NODE_ENV,
        hostname,
        envToken: null,
        storageToken,
    });
}

api.interceptors.request.use((config) => {
    const devToken = readDevAuthToken();
    if (devToken) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${devToken}`;
    }
    return config;
});

// Auth
export const exchangeSession = (session_id, ref) => api.post("/auth/google-session", { session_id, ref: ref || undefined });
export const fetchMe = () => api.get("/auth/me");
export const logout = () => api.post("/auth/logout");

// Exercises
export const listExercises = () => api.get("/exercises");
export const getExercise = (id) => api.get(`/exercises/${id}`);

// Admin exercises
export const adminListExercises = () => api.get("/admin/exercises");
export const adminCreateExercise = (body) => api.post("/admin/exercises", body);
export const adminUpdateExercise = (id, body) => api.put(`/admin/exercises/${id}`, body);
export const adminDeleteExercise = (id) => api.delete(`/admin/exercises/${id}`);
export const adminListUsers = () => api.get("/admin/users");
export const adminPromoteUser = (user_id) => api.post(`/admin/promote/${user_id}`);

// Workouts
export const startWorkout = (exercise_id) => api.post("/workouts/start", { exercise_id });
export const updateWorkout = (id, body) => api.patch(`/workouts/${id}`, body);
export const endWorkout = (id, body) => api.post(`/workouts/${id}/end`, body);
export const listWorkouts = (limit) => api.get(limit ? `/workouts?limit=${limit}` : "/workouts");
export const getWorkout = (id) => api.get(`/workouts/${id}`);

// Tasks
export const tasksToday = () => api.get("/tasks/today");
export const completeTask = (id) => api.post(`/tasks/${id}/complete`);

// Plans
export const listPlans = () => api.get("/plans");
export const enrollPlan = (id) => api.post(`/plans/${id}/enroll`);

// Competitions
export const listCompetitions = () => api.get("/competitions");
export const joinCompetition = (id) => api.post(`/competitions/${id}/join`);

// Leaderboard
export const leaderboard = (period = "week") => api.get(`/leaderboard?period=${period}`);

// Stats
export const dashboardStats = () => api.get("/stats/dashboard");
export const personalBests = () => api.get("/stats/bests");
export const adminAnalytics = (days = 30) => api.get(`/admin/analytics?days=${days}`);
export const exerciseBest = (exerciseId) => api.get(`/stats/bests/${exerciseId}`);
export const listTransactions = () => api.get("/transactions");

// Streak
export const streakInfo = () => api.get("/streak/info");
export const streakHeat = (days = 30) => api.get(`/streak/heat?days=${days}`);
export const buyShield = () => api.post("/streak/buy-shield");
export const reviveStreak = () => api.post("/streak/revive");

// Goals (weekly contract: daily routine + weekly volume)
export const getGoals = () => api.get("/goals");
export const saveGoals = (body) => api.put("/goals", body);

// Profile
export const getProfile = () => api.get("/profile");
export const updateProfile = (body) => api.patch("/profile", body);

// Push (PWA web-push)
export const pushPublicKey = () => api.get("/push/public-key");
export const pushSubscribe = (subscription) => api.post("/push/subscribe", subscription);
export const pushUnsubscribe = (endpoint) => api.post("/push/unsubscribe", { endpoint });
export const adminPushStats = () => api.get("/admin/push/stats");
export const adminPushBroadcast = (body) => api.post("/admin/push/broadcast", body);

// Challenges ("beat my score" links)
export const createChallenge = (workout_id) => api.post("/challenges", { workout_id });
export const getChallenge = (id) => api.get(`/challenges/${id}`);
export const attemptChallenge = (id, workout_id) => api.post(`/challenges/${id}/attempt`, { workout_id });

// Daily community challenge + weekly league
export const dailyChallenge = () => api.get("/daily");
export const getLeague = () => api.get("/league");

// Public + referrals
export const publicProfile = (username) => api.get(`/public/profile/${username}`);
export const referralStats = () => api.get("/profile/referrals");
